/**
 * Pinterest-Style Masonry Grid Script
 * Dynamically adjusts grid items based on content height
 */

class MasonryGrid {
  constructor(gridSelector = '.product-grid') {
    this.grid = document.querySelector(gridSelector);
    this.items = [];
    this.rowHeight = 10; // Must match grid-auto-rows in CSS
    this.gap = 16; // Must match grid-gap in CSS
    
    if (this.grid) {
      this.init();
    }
  }

  init() {
    // Get all grid items
    this.items = Array.from(this.grid.children);
    
    // Wait for images to load
    this.waitForImages().then(() => {
      this.layoutItems();
      this.addResizeListener();
    });

    // Re-layout on new items added (for infinite scroll)
    this.observeNewItems();
  }

  waitForImages() {
    const images = this.grid.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
      if (img.complete) {
        return Promise.resolve();
      }
      return new Promise(resolve => {
        img.addEventListener('load', resolve);
        img.addEventListener('error', resolve);
      });
    });
    
    return Promise.all(imagePromises);
  }

  layoutItems() {
    this.items.forEach(item => {
      const content = item.querySelector('.card__inner, .card') || item;
      const contentHeight = content.getBoundingClientRect().height;
      const rowSpan = Math.ceil((contentHeight + this.gap) / (this.rowHeight + this.gap));
      
      item.style.gridRowEnd = `span ${rowSpan}`;
    });
  }

  addResizeListener() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.layoutItems();
      }, 200);
    });
  }

  observeNewItems() {
    const observer = new MutationObserver(mutations => {
      let shouldRelayout = false;
      
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1 && this.grid.contains(node)) {
              shouldRelayout = true;
              this.items.push(node);
            }
          });
        }
      });
      
      if (shouldRelayout) {
        this.waitForImages().then(() => {
          this.layoutItems();
        });
      }
    });
    
    observer.observe(this.grid, {
      childList: true,
      subtree: false
    });
  }

  relayout() {
    this.items = Array.from(this.grid.children);
    this.waitForImages().then(() => {
      this.layoutItems();
    });
  }
}

// Initialize masonry grid when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMasonry);
} else {
  initMasonry();
}

function initMasonry() {
  // Initialize main grid
  const mainMasonry = new MasonryGrid('.product-grid');
  
  // Make it globally accessible for potential re-layouts
  window.masonryGrid = mainMasonry;
  
  // Handle filter/sort updates
  document.addEventListener('click', (e) => {
    // Check if clicked element is a filter or sort button
    if (e.target.closest('.facet-checkbox, .facet-filters__sort')) {
      setTimeout(() => {
        if (window.masonryGrid) {
          window.masonryGrid.relayout();
        }
      }, 500);
    }
  });
  
  // Handle infinite scroll if present
  if (window.IntersectionObserver) {
    const loadMore = document.querySelector('.load-more, [data-load-more]');
    if (loadMore) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              if (window.masonryGrid) {
                window.masonryGrid.relayout();
              }
            }, 500);
          }
        });
      });
      observer.observe(loadMore);
    }
  }
}

// Add Pinterest-style hover effects
document.addEventListener('DOMContentLoaded', () => {
  const productCards = document.querySelectorAll('.product-card-wrapper');
  
  productCards.forEach(card => {
    // Add "Save" badge on hover (Pinterest style)
    if (!card.querySelector('.card__badge')) {
      const badge = document.createElement('div');
      badge.className = 'card__badge';
      badge.textContent = 'Save';
      card.appendChild(badge);
    }
    
    // Enhance click behavior
    card.addEventListener('click', (e) => {
      if (!e.target.closest('a')) {
        const link = card.querySelector('a');
        if (link) {
          window.location.href = link.href;
        }
      }
    });
  });
});

// Utility: Update grid on theme editor changes (for Shopify theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    setTimeout(() => {
      const newMasonry = new MasonryGrid('.product-grid');
      window.masonryGrid = newMasonry;
    }, 100);
  });
  
  document.addEventListener('shopify:section:reorder', () => {
    if (window.masonryGrid) {
      window.masonryGrid.relayout();
    }
  });
}